'use client';

import { useEffect, useRef } from 'react';

import type { MysteryRoomLocalRuntime } from './core/localRuntime';
import type { MrMatchSnapshot } from './net/mysteryRoomApi';
import type { MysteryRoomMapDefinition } from './world/mapTypes';
import { createBootScene } from './scenes/BootScene';
import { createLabScene } from './scenes/LabScene';
import { createUIScene } from './scenes/UIScene';
import { hasInventoryFlashlight } from './systems/interactionSystem';
import { createMysteryRoomClientSimulationMirror } from './net/clientSimulationMirror';

type ServerProps = {
  mode?: 'server';
  snapshot: MrMatchSnapshot | null;
  selfPlayerId: string | null;
  serverNowMs: number;
  mapDefinition?: MysteryRoomMapDefinition;
  activeInventoryItemId?: string | null;
  flashlightEnabled?: boolean;
  frameless?: boolean;
  getPredictedRuntime?: () => MysteryRoomLocalRuntime | null;
  advancePredictedRuntime?: (dtMs: number) => void;
  onPredictedMoveVectorChange?: (x: number, y: number) => void;
  onPredictedPointerTarget?: (x: number, y: number) => void;
  onMoveIntent?: (x: number, y: number) => void;
  onShootIntent?: (x: number, y: number) => void;
  onInteractIntent?: (interactableId: string) => void;
};

type LocalProps = {
  mode: 'local';
  localRuntime: MysteryRoomLocalRuntime;
  getLocalRuntime?: () => MysteryRoomLocalRuntime | null;
  frameless?: boolean;
  onLocalMoveVectorChange?: (x: number, y: number) => void;
  onLocalPointerTarget?: (x: number, y: number) => void;
  onLocalInteract?: (interactableId: string) => void;
  onLocalShoot?: (x: number, y: number) => void;
};

type Props = ServerProps | LocalProps;

type MysteryRoomGameWindow = typeof window & {
  render_game_to_text?: () => string;
  advanceTime?: (ms?: number) => void;
  __mysteryRoomAudioDebug?: unknown;
  __mysteryRoomCameraDebug?: unknown;
};

export function installMysteryRoomAdvanceTimeFallback(
  gameWindow: { advanceTime?: (ms?: number) => void },
  fallback: (ms?: number) => void,
) {
  if (typeof gameWindow.advanceTime === 'function') {
    return () => undefined;
  }

  gameWindow.advanceTime = fallback;
  return () => {
    if (gameWindow.advanceTime === fallback) {
      delete gameWindow.advanceTime;
    }
  };
}

export default function PhaserMount(props: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<any>(null);

  const snapshotRef = useRef<MrMatchSnapshot | null>(props.mode === 'local' ? null : props.snapshot);
  const selfPlayerIdRef = useRef<string | null>(props.mode === 'local' ? 'local-hero' : props.selfPlayerId);
  const serverNowMsRef = useRef<number>(props.mode === 'local' ? 0 : props.serverNowMs);
  const mapDefinitionRef = useRef<MysteryRoomMapDefinition | undefined>(props.mode === 'local' ? undefined : props.mapDefinition);
  const serverEquipmentRef = useRef<{ activeInventoryItemId: string | null; flashlightEnabled: boolean }>({
    activeInventoryItemId: props.mode === 'local' ? null : props.activeInventoryItemId ?? null,
    flashlightEnabled: props.mode === 'local' ? false : props.flashlightEnabled ?? false,
  });
  const onMoveIntentRef = useRef<((x: number, y: number) => void) | undefined>(props.mode === 'local' ? undefined : props.onMoveIntent);
  const onShootIntentRef = useRef<((x: number, y: number) => void) | undefined>(props.mode === 'local' ? undefined : props.onShootIntent);
  const onInteractIntentRef = useRef<((interactableId: string) => void) | undefined>(
    props.mode === 'local' ? undefined : props.onInteractIntent,
  );
  const getPredictedRuntimeRef = useRef<(() => MysteryRoomLocalRuntime | null) | undefined>(
    props.mode === 'local' ? undefined : props.getPredictedRuntime,
  );
  const advancePredictedRuntimeRef = useRef<((dtMs: number) => void) | undefined>(
    props.mode === 'local' ? undefined : props.advancePredictedRuntime,
  );
  const getLocalRuntimeRef = useRef<(() => MysteryRoomLocalRuntime | null) | undefined>(
    props.mode === 'local' ? props.getLocalRuntime : undefined,
  );
  const localRuntimeRef = useRef<MysteryRoomLocalRuntime | null>(props.mode === 'local' ? props.localRuntime : null);
  const onLocalMoveVectorChangeRef = useRef<((x: number, y: number) => void) | undefined>(
    props.mode === 'local' ? props.onLocalMoveVectorChange : props.onPredictedMoveVectorChange,
  );
  const onLocalPointerTargetRef = useRef<((x: number, y: number) => void) | undefined>(
    props.mode === 'local' ? props.onLocalPointerTarget : props.onPredictedPointerTarget,
  );
  const onLocalInteractRef = useRef<((interactableId: string) => void) | undefined>(
    props.mode === 'local' ? props.onLocalInteract : undefined,
  );
  const onLocalShootRef = useRef<((x: number, y: number) => void) | undefined>(
    props.mode === 'local' ? props.onLocalShoot : undefined,
  );
  const readLocalRuntime = () => getPredictedRuntimeRef.current?.() ?? getLocalRuntimeRef.current?.() ?? localRuntimeRef.current;

  if (props.mode === 'local') {
    getLocalRuntimeRef.current = props.getLocalRuntime;
    const currentLocalRuntime = props.getLocalRuntime?.() ?? props.localRuntime;
    localRuntimeRef.current = currentLocalRuntime;
    onLocalMoveVectorChangeRef.current = props.onLocalMoveVectorChange;
    onLocalPointerTargetRef.current = props.onLocalPointerTarget;
    onLocalInteractRef.current = props.onLocalInteract;
    onLocalShootRef.current = props.onLocalShoot;
    snapshotRef.current = null;
    selfPlayerIdRef.current = 'local-hero';
    serverNowMsRef.current = 0;
    mapDefinitionRef.current = currentLocalRuntime.map;
    serverEquipmentRef.current = { activeInventoryItemId: null, flashlightEnabled: false };
    onMoveIntentRef.current = undefined;
    onShootIntentRef.current = undefined;
    onInteractIntentRef.current = undefined;
    getPredictedRuntimeRef.current = undefined;
    advancePredictedRuntimeRef.current = undefined;
  } else {
    snapshotRef.current = props.snapshot;
    selfPlayerIdRef.current = props.selfPlayerId;
    serverNowMsRef.current = props.serverNowMs;
    mapDefinitionRef.current = props.mapDefinition;
    serverEquipmentRef.current = {
      activeInventoryItemId: props.activeInventoryItemId ?? null,
      flashlightEnabled: props.flashlightEnabled ?? false,
    };
    onMoveIntentRef.current = props.onMoveIntent;
    onShootIntentRef.current = props.onShootIntent;
    onInteractIntentRef.current = props.onInteractIntent;
    getPredictedRuntimeRef.current = props.getPredictedRuntime;
    getLocalRuntimeRef.current = undefined;
    advancePredictedRuntimeRef.current = props.advancePredictedRuntime;
    localRuntimeRef.current =
      props.getPredictedRuntime?.() ??
      createMysteryRoomClientSimulationMirror({
        snapshot: props.snapshot,
        selfPlayerId: props.selfPlayerId,
        serverNowMs: props.serverNowMs,
        mapDefinition: props.mapDefinition,
        activeInventoryItemId: props.activeInventoryItemId ?? null,
        flashlightEnabled: props.flashlightEnabled ?? false,
        previous: localRuntimeRef.current,
      });
    onLocalMoveVectorChangeRef.current = props.onPredictedMoveVectorChange;
    onLocalPointerTargetRef.current = props.onPredictedPointerTarget;
    onLocalInteractRef.current = props.onInteractIntent;
    onLocalShootRef.current = undefined;
  }

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      if (!containerRef.current || gameRef.current) {
        return;
      }

      const Phaser = await import('phaser');
      if (cancelled || !containerRef.current) {
        return;
      }

      const adapter = {
        getMode: () => props.mode ?? 'server',
        getSnapshot: () => snapshotRef.current,
        getSelfPlayerId: () => selfPlayerIdRef.current,
        getServerNowMs: () => serverNowMsRef.current,
        getMapDefinition: () => mapDefinitionRef.current,
        getServerEquipment: () => serverEquipmentRef.current,
        onMoveIntent: (x: number, y: number) => onMoveIntentRef.current?.(x, y),
        onShootIntent: (x: number, y: number) => onShootIntentRef.current?.(x, y),
        onInteractIntent: (interactableId: string) => onInteractIntentRef.current?.(interactableId),
        getLocalRuntime: () => readLocalRuntime(),
        advancePredictedRuntime: (dtMs: number) => advancePredictedRuntimeRef.current?.(dtMs),
        onLocalMoveVectorChange: (x: number, y: number) => onLocalMoveVectorChangeRef.current?.(x, y),
        onLocalPointerTarget: (x: number, y: number) => onLocalPointerTargetRef.current?.(x, y),
        onLocalInteract: (interactableId: string) => onLocalInteractRef.current?.(interactableId),
        onLocalShoot: (x: number, y: number) => onLocalShootRef.current?.(x, y),
      };

      const BootScene = createBootScene(Phaser);
      const LabScene = createLabScene(Phaser, adapter);
      const UIScene = createUIScene(Phaser, adapter);

      const game = new Phaser.Game({
        type: Phaser.AUTO,
        parent: containerRef.current,
        width: containerRef.current.clientWidth || 1280,
        height: containerRef.current.clientHeight || 720,
        transparent: true,
        backgroundColor: '#050b1a',
        render: {
          antialias: true,
          antialiasGL: true,
          pixelArt: false,
          roundPixels: true,
        },
        dom: {
          createContainer: true,
        },
        physics: {
          default: 'arcade',
          arcade: {
            gravity: { y: 0, x: 0 },
            debug: false,
          },
        },
        scene: [new BootScene(), new LabScene(), new UIScene()],
        scale: {
          mode: Phaser.Scale.RESIZE,
          autoCenter: Phaser.Scale.CENTER_BOTH,
        },
      });

      gameRef.current = game;
      const gameWindow = window as MysteryRoomGameWindow;
      const renderGameToText = () => {
        const localRuntime = readLocalRuntime();
        if (localRuntime) {
          const heroRoom = localRuntime.map.rooms.find((room) => room.id === localRuntime.hero.zoneId);

          return JSON.stringify({
            coordinateSystem: 'world pixels, origin top-left, x right, y down',
            mode: (props.mode ?? 'server') === 'server' ? 'network-client-simulation' : 'local-core-slice',
            gameStatus: localRuntime.gameStatus,
            serverVerified: (props.mode ?? 'server') === 'server',
            map: {
              id: localRuntime.map.id,
              title: localRuntime.map.title,
            },
            playerStatus: {
              zoneTitle: heroRoom?.title ?? 'unknown',
              alertState: localRuntime.hero.alertState,
              alive: localRuntime.hero.alive,
              healthPercent: Math.round((localRuntime.hero.health / Math.max(1, localRuntime.hero.maxHealth)) * 100),
              hasFlashlight: hasInventoryFlashlight(localRuntime),
              flashlightEnabled: localRuntime.flashlightEnabled,
            },
            publicCounts: {
              openDoors: localRuntime.doors.filter((door) => door.state === 'open').length,
              enabledLights: localRuntime.lights.filter((light) => light.enabled).length,
              incidents: localRuntime.incidents.length,
              activeInventorySlots: localRuntime.inventory.filter((item) => (item.quantity ?? 1) > 0).length,
            },
            combat: {
              projectiles: localRuntime.projectiles.length,
              effects: localRuntime.impactEffects.length,
              bloodEffects: localRuntime.impactEffects.filter((effect) => effect.kind.startsWith('blood_')).length,
            },
            security: {
              trespassActive: localRuntime.security.trespass !== null,
              trespassEscalated: localRuntime.security.trespass
                ? localRuntime.security.trespass.escalatedAtMs !== null
                : false,
              bodyReportCount: localRuntime.security.bodyReports.length,
            },
            worldAlarm: {
              state: localRuntime.worldAlarm.state,
            },
          });
        }

        const snapshot = snapshotRef.current;
        return JSON.stringify({
          coordinateSystem: 'server world pixels, origin top-left, x right, y down',
          mode: 'server',
          mapId: snapshot?.mapId ?? mapDefinitionRef.current?.id ?? 'lisad_9_night_shift',
          players: snapshot?.players?.length ?? 0,
          markers: snapshot?.visibleMarkers?.length ?? 0,
          activeInventoryItemId: serverEquipmentRef.current.activeInventoryItemId,
          flashlightEnabled: serverEquipmentRef.current.flashlightEnabled,
        });
      };
      gameWindow.render_game_to_text = renderGameToText;
      const advanceTime = () => undefined;
      const cleanupAdvanceTime = installMysteryRoomAdvanceTimeFallback(gameWindow, advanceTime);
      const onResize = () => {
        if (!containerRef.current || !gameRef.current) {
          return;
        }
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        gameRef.current.scale.resize(width, height);
      };

      window.addEventListener('resize', onResize);
      game.events.once(Phaser.Core.Events.DESTROY, () => {
        window.removeEventListener('resize', onResize);
        if (gameWindow.render_game_to_text === renderGameToText) {
          delete gameWindow.render_game_to_text;
        }
        cleanupAdvanceTime();
      });
    }

    void boot();

    return () => {
      cancelled = true;
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, [props.mode]);

  return (
    <div
      ref={containerRef}
      className={
        props.frameless
          ? 'h-full w-full overflow-hidden bg-site-surface'
          : 'h-full w-full overflow-hidden rounded-[20px] border border-site-border bg-site-surface'
      }
    />
  );
}
